import { useMemo } from "react";
import { Link } from "react-router-dom";
import SiteHeader from "@/components/feature/SiteHeader";
import SiteFooter from "@/components/feature/SiteFooter";
import Breadcrumbs from "@/components/feature/Breadcrumbs";
import BackToTop from "@/components/feature/BackToTop";
import SectionHeading from "@/components/feature/SectionHeading";
import { useSeo } from "@/hooks/useSeo";
import { verdictMeta } from "@/mocks/types";
import RatingScale from "./components/RatingScale";
import Methodology from "./components/Methodology";
import CaseStudies from "./components/CaseStudies";
import DebunkList from "./components/DebunkList";

export default function VerifyPage() {
  const jsonLd = useMemo(
    () => ({
      "@context": "https://schema.org",
      "@type": "WebPage",
      name: "求证中心｜爆料可信度评级标准与真假对照 · 拾真社",
      url: "https://hdlyjgs.cn/verify",
      inLanguage: "zh-CN",
      description:
        "拾真社求证中心公开可信度评级标准、求证方法论与典型真假对照案例，说明每条爆料如何从材料核验到给出结论。",
      mainEntity: {
        "@type": "ItemList",
        name: "可信度评级标准",
        itemListElement: Object.values(verdictMeta).map((meta, index) => ({
          "@type": "ListItem",
          position: index + 1,
          name: meta.label,
          description: meta.desc,
        })),
      },
    }),
    [],
  );

  useSeo({
    title: "求证中心｜独家爆料可信度评级与真假对照标准 · 拾真社",
    description:
      "拾真社求证中心公开可信度评级标准、四步求证方法论与典型真假对照案例，说明爆料如何从原始材料核验走到明确结论，帮读者独立判断信息可信度。",
    keywords: "独家爆料求证,爆料可信度,真假对照,实锤证据,信息来源核实",
    path: "/verify",
    jsonLd,
  });

  return (
    <div className="min-h-screen bg-background-50">
      <SiteHeader activePath="/verify" />
      <Breadcrumbs items={[{ label: "求证中心" }]} />

      <section className="border-b border-background-200 bg-foreground-950">
        <div className="mx-auto max-w-[1200px] px-4 py-12 md:px-8 md:py-16">
          <p className="flex items-center gap-2 font-label text-[11px] uppercase tracking-[0.2em] text-accent-400">
            <span className="inline-block h-px w-6 bg-accent-400" aria-hidden="true"></span>
            求证中心
          </p>
          <h1 className="mt-5 max-w-3xl font-heading text-3xl font-bold leading-snug text-background-50 md:text-4xl">
            先说清楚我们怎么判断真假
          </h1>
          <p className="mt-5 max-w-2xl text-sm leading-relaxed text-background-200 md:text-base">
            求证不该是个黑箱。这里公开我们使用的评级标准、求证步骤，以及典型的真假对照案例。
            看懂规则之后，你也可以用同样的方法去核对别的信息。
          </p>
        </div>
      </section>

      <main className="mx-auto max-w-[1200px] px-4 py-12 md:px-8 md:py-16">
        <section aria-labelledby="scale-heading" className="mb-16">
          <div id="scale-heading">
            <SectionHeading
              eyebrow="评级标准"
              title="五种结论，对应五种不同的证据状态"
              desc="我们不会只给一个『真』或『假』。材料支持到哪一步，结论就写到哪一步。"
            />
          </div>
          <RatingScale />
        </section>

        <section aria-labelledby="method-heading" className="mb-16">
          <div id="method-heading">
            <SectionHeading
              eyebrow="求证方法论"
              title="四条一直遵守的底线"
              desc="这些原则决定了哪些线索会被立档、哪些只会停留在线索阶段。"
            />
          </div>
          <Methodology />
        </section>

        <section aria-labelledby="case-heading" className="mb-16">
          <div id="case-heading">
            <SectionHeading
              eyebrow="真假对照"
              title="三则典型案例，看看差异出在哪"
              desc="同一件事的说法与事实之间，往往只差几个被忽略的细节。"
              action={{ label: "进入爆料库", to: "/scoops" }}
            />
          </div>
          <CaseStudies />
        </section>

        <section aria-labelledby="debunk-heading" className="mb-14">
          <div id="debunk-heading">
            <SectionHeading
              eyebrow="辨析清单"
              title="已辟谣与仍待求证的档案"
              desc="这些档案提醒我们：有些说法已经被证伪，另一些则需要更多材料才能下结论。"
            />
          </div>
          <DebunkList />
        </section>

        <section className="overflow-hidden rounded-lg border border-background-200 bg-background-100">
          <div className="grid grid-cols-1 gap-6 p-6 md:grid-cols-[1.4fr_1fr] md:items-center md:p-10">
            <div>
              <h2 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">
                对某条档案的评级有疑问？
              </h2>
              <p className="mt-3 max-w-xl text-sm leading-relaxed text-foreground-600">
                如果你掌握了我们没有的材料，或者认为某条结论存在偏差，欢迎直接指出。纠错和补充材料一样重要。
              </p>
            </div>
            <div className="flex flex-col gap-3 sm:flex-row md:justify-end">
              <Link
                to="/contact"
                className="inline-flex items-center justify-center gap-2 rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 whitespace-nowrap"
              >
                <i className="ri-flag-line" aria-hidden="true"></i>
                提交纠错
              </Link>
              <Link
                to="/about"
                className="inline-flex items-center justify-center gap-2 rounded-md border border-foreground-300 px-5 py-2.5 text-sm text-foreground-800 transition-colors hover:border-primary-400 hover:text-primary-600 whitespace-nowrap"
              >
                了解内容原则
              </Link>
            </div>
          </div>
        </section>
      </main>
      <SiteFooter />
      <BackToTop />
    </div>
  );
}